<template>
  <div>
    <v-text-field
      v-bind:label="$t('Name')"
      v-model="wizard.organization.name"
      :error-messages="organizationNameErrors"
      @input="validator.wizard.organization.name.$touch()"
      @blur="validator.wizard.organization.name.$touch()"
    ></v-text-field>
    <v-row>
      <v-col>
        <v-text-field
          type="number"
          v-bind:label="$t('FullAccessUsers')"
          v-model="wizard.organization.fullUsersLimit"
          :error-messages="fullUsersLimitErrors"
          @input="validator.wizard.organization.fullUsersLimit.$touch()"
          @blur="validator.wizard.organization.fullUsersLimit.$touch()"
        ></v-text-field>
      </v-col>
      <v-col>
        <v-text-field
          type="number"
          v-bind:label="$t('ReadonlyUsers')"
          v-model="wizard.organization.readonlyUsersLimit"
          :error-messages="readonlyUsersLimitErrors"
          @input="validator.wizard.organization.readonlyUsersLimit.$touch()"
          @blur="validator.wizard.organization.readonlyUsersLimit.$touch()"
        ></v-text-field>
      </v-col>
      <v-col>
        <v-text-field
          type="number"
          v-bind:label="$t('ConcurrentUsers')"
          v-model="wizard.organization.concurrentUsersLimit"
          :error-messages="concurrentUsersLimitErrors"
          @input="validator.wizard.organization.concurrentUsersLimit.$touch()"
          @blur="validator.wizard.organization.concurrentUsersLimit.$touch()"
        ></v-text-field>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-text-field
          type="number"
          v-bind:label="`${$t('StorageQuota')} (GB)`"
          v-model="wizard.organization.quota"
          :error-messages="quotaErrors"
          @input="validator.wizard.organization.quota.$touch()"
          @blur="validator.wizard.organization.quota.$touch()"
        ></v-text-field>
      </v-col>
      <v-col>
        <v-text-field
          type="number"
          v-bind:label="$t('RegisteredDevices')"
          v-model="wizard.organization.registeredDevicesLimit"
          :error-messages="registeredDevicesLimitErrors"
          @input="validator.wizard.organization.registeredDevicesLimit.$touch()"
          @blur="validator.wizard.organization.registeredDevicesLimit.$touch()"
        ></v-text-field>
      </v-col>
      <v-col>
        <v-text-field
          type="number"
          v-bind:label="$t('TwoFactorAuthenticationUsers')"
          v-model="wizard.organization.twoFactorAuthenticationUsersLimit"
          :error-messages="twoFactorAuthenticationUsersLimitErrors"
          @input="
            validator.wizard.organization.twoFactorAuthenticationUsersLimit.$touch()
          "
          @blur="
            validator.wizard.organization.twoFactorAuthenticationUsersLimit.$touch()
          "
        ></v-text-field>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-switch
          v-bind:label="$t('UseAutorecognitionLicense')"
          color="primary"
          v-model="wizard.organization.autorecognitionEnabled"
        ></v-switch>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        Logo
        <vue-dropzone
          id="dropZone"
          :ref="dropZoneKey"
          :options="dropzoneOptions"
          @vdropzone-success="onFileSuccess"
          @vdropzone-removed-file="onFileRemoved"
        ></vue-dropzone>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import Attachment from '@/ecm/models/document/Attachment';
import vue2Dropzone from 'vue2-dropzone';
import 'vue2-dropzone/dist/vue2Dropzone.min.css';
export default {
    props: {
        wizard: {
            type: Object,
            default: () => ({}),
        },
        validator: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        const url = `${this.$store.getters['account/apiUrl']}/api/organization/upload-logo/-1`;

        return {
            dropZoneKey: 'drop-zone-wizard-org-logo',
            dropzoneOptions: {
                url: url,
                thumbnailWidth: 150,
                timeout: 30000, //30 seconds
                maxFilesize: 10, //10 MB
                maxFiles: 1,
                destroyDropzone: true,
                headers: { Authorization: 'bearer ' + this.$store.getters['account/token'] },
                addRemoveLinks: true,
                acceptedFiles: 'image/*',
                dictDefaultMessage: this.$t('DropzoneMessage'),
                dictRemoveFile: this.$t('Remove'),
                dictInvalidFileType: this.$t('DropzoneInvalidFileType'),
            },
        };
    },
    components: {
        vueDropzone: vue2Dropzone,
    },
    computed: {
        organizationNameErrors() {
            const errors = [];
            if (!this.validator.wizard.organization.name.$dirty) return errors;
            !this.validator.wizard.organization.name.required &&
                errors.push(this.$t('RequiredField'));
            return errors;
        },
        quotaErrors() {
            const errors = [];
            if (!this.validator.wizard.organization.quota.$dirty) return errors;
            !this.validator.wizard.organization.quota.required &&
                errors.push(
                    `${this.$t('RequiredField')} (${this.$t('FieldTypeNumeric')})`
                );
            !this.validator.wizard.organization.quota.minValue &&
                errors.push(`${this.$t('MinValue')}: 1`);
            return errors;
        },
        fullUsersLimitErrors() {
            const errors = [];
            if (!this.validator.wizard.organization.fullUsersLimit.$dirty)
                return errors;
            !this.validator.wizard.organization.fullUsersLimit.required &&
                errors.push(
                    `${this.$t('RequiredField')} (${this.$t('FieldTypeNumeric')})`
                );
            !this.validator.wizard.organization.fullUsersLimit.minValue &&
                errors.push(`${this.$t('MinValue')}: 0`);
            return errors;
        },
        readonlyUsersLimitErrors() {
            const errors = [];
            if (!this.validator.wizard.organization.readonlyUsersLimit.$dirty)
                return errors;
            !this.validator.wizard.organization.readonlyUsersLimit.required &&
                errors.push(
                    `${this.$t('RequiredField')} (${this.$t('FieldTypeNumeric')})`
                );
            !this.validator.wizard.organization.readonlyUsersLimit.minValue &&
                errors.push(`${this.$t('MinValue')}: 0`);
            return errors;
        },
        concurrentUsersLimitErrors() {
            const errors = [];
            if (!this.validator.wizard.organization.concurrentUsersLimit.$dirty)
                return errors;
            !this.validator.wizard.organization.concurrentUsersLimit.required &&
                errors.push(
                    `${this.$t('RequiredField')} (${this.$t('FieldTypeNumeric')})`
                );
            !this.validator.wizard.organization.concurrentUsersLimit.minValue &&
                errors.push(`${this.$t('MinValue')}: 1`);
            return errors;
        },
        registeredDevicesLimitErrors() {
            const errors = [];
            if (!this.validator.wizard.organization.registeredDevicesLimit.$dirty)
                return errors;
            !this.validator.wizard.organization.registeredDevicesLimit.required &&
                errors.push(
                    `${this.$t('RequiredField')} (${this.$t('FieldTypeNumeric')})`
                );
            !this.validator.wizard.organization.registeredDevicesLimit.minValue &&
                errors.push(`${this.$t('MinValue')}: 0`);
            return errors;
        },
        twoFactorAuthenticationUsersLimitErrors() {
            const errors = [];
            if (
                !this.validator.wizard.organization.twoFactorAuthenticationUsersLimit
                    .$dirty
            )
                return errors;
            !this.validator.wizard.organization.twoFactorAuthenticationUsersLimit
                .required &&
                errors.push(
                    `${this.$t('RequiredField')} (${this.$t('FieldTypeNumeric')})`
                );
            !this.validator.wizard.organization.twoFactorAuthenticationUsersLimit
                .minValue && errors.push(`${this.$t('MinValue')}: 0`);
            return errors;
        },
        orgName() {
            return this.wizard.organization.name;
        },
    },
    watch: {
        orgName(val) {
            this.setNormalizedSuffix(val);
        },
    },
    methods: {
        setNormalizedSuffix(val) {
            var normalized = val
                .replace(/[^a-zA-Z]+/g, '')
                .trim()
                .toLowerCase();
            this.wizard.users[0].loginSuffix = normalized;
        },
        onFileSuccess(file, response) {
            var attachment = new Attachment();
            attachment.fileId = response;
            attachment.fileName = file.name;
            this.wizard.organization.logo = attachment;
        },
        onFileRemoved(file) {
            this.wizard.organization.logo = null;
        },
    },
};
</script>

<style>
</style>